import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import FaceIdentityFlowNav from '../../face-identity/components/FaceIdentityFlowNav';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import { useFaceIdentityScrollShell } from '../../face-identity/hooks/useFaceIdentityScrollShell';
import { readFaceProfileSnapshot } from '../../face-identity/utils/faceIdentityApi';
import { buildKioskNavState } from '../../face-identity/utils/kioskNavState';
import { SALON_SERVICE_LOBBY_PATH } from '../config/salonApp';
import { captureDeviceInfo } from '../utils/deviceCapture';
import { resetWalkInGuestCheckinState, submitGuestLobbyCheckin } from '../utils/guestSalonCheckin';
import {
  VISIT_INTENT_ANALYZE,
  VISIT_INTENT_SERVICE,
  visitIntentLabel,
} from '../utils/visitIntent';
import { KIOSK_MODE_DESK, KIOSK_MODE_GUEST, setSalonKioskMode } from '../utils/salonKioskMode';
import '../../face-identity/styles/FaceIdentityLuxTheme.css';
import '../../face-identity/styles/FaceIdentityScrollShell.css';
import './VisitIntentPage.css';
import './ServiceSessionPage.css';

function faceIdentityFromRouteState(state) {
  const faceUserId = String(state?.faceUserId || '').trim();
  const profileId = String(state?.faceProfile?.faceUserId || state?.faceProfile?.user?.id || '').trim();
  const userId = String(state?.user?.id || '').trim();
  const id = faceUserId || profileId || userId;
  return id && !id.startsWith('checkin:') ? id : '';
}

export default function GuestVisitIntentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  useFaceIdentityScrollShell();
  const navState = buildKioskNavState(location.state);

  const faceFromThisVisit = useMemo(
    () => faceIdentityFromRouteState(location.state),
    [location.state],
  );
  const hasProfile = Boolean(faceFromThisVisit);

  const profileBundle = useMemo(() => {
    if (!hasProfile) return { user: {}, snapshot: {} };
    const snapshot = location.state?.faceProfile || readFaceProfileSnapshot() || {};
    const user = snapshot.user || location.state?.user || {};
    return { user, snapshot };
  }, [hasProfile, location.state]);

  const [walkInName, setWalkInName] = useState(() =>
    String(location.state?.walkInName || '').trim(),
  );
  const [walkInMobile, setWalkInMobile] = useState(() =>
    String(location.state?.walkInMobile || '').trim(),
  );

  const displayName = useMemo(() => {
    if (hasProfile) {
      const u = profileBundle.user;
      return String(u?.name || u?.full_name || 'Guest').trim() || 'Guest';
    }
    return String(walkInName || '').trim() || 'Guest';
  }, [hasProfile, profileBundle.user, walkInName]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [doneIntent, setDoneIntent] = useState(null);
  const [selectedIntent, setSelectedIntent] = useState(null);

  useEffect(() => {
    setSalonKioskMode(KIOSK_MODE_GUEST);
    if (!faceFromThisVisit) {
      resetWalkInGuestCheckinState();
      setWalkInName((prev) => (location.state?.walkInName ? prev : ''));
      setWalkInMobile((prev) => (location.state?.walkInMobile ? prev : ''));
      setDoneIntent(null);
      setSelectedIntent(null);
    }
  }, [faceFromThisVisit, location.state?.walkInName, location.state?.walkInMobile]);

  const goScan = () => {
    navigate('/nyafaz-ep/salon/scan', {
      replace: false,
      state: {
        ...navState,
        next: '/nyafaz-ep/salon/guest-intent',
        mode: 'salon',
        deviceInfo: location.state?.deviceInfo || captureDeviceInfo(),
      },
    });
  };

  const nameReady = hasProfile || Boolean(String(walkInName || '').trim());
  const canConfirm = Boolean(selectedIntent) && nameReady && !busy;

  const confirmCheckin = useCallback(async () => {
    if (!canConfirm || !selectedIntent) return;
    if (!hasProfile && !String(walkInName || '').trim()) {
      setError('Please enter your name');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await submitGuestLobbyCheckin({
        visitIntent: selectedIntent,
        customerName: hasProfile ? displayName : walkInName,
        mobile: hasProfile
          ? profileBundle.user?.mobile || profileBundle.user?.phone
          : walkInMobile,
        faceUserId: hasProfile ? faceFromThisVisit : '',
        user: hasProfile ? profileBundle.user : null,
        snapshot: hasProfile ? profileBundle.snapshot : null,
        source: hasProfile ? 'salon_guest_face' : 'salon_guest_walkin',
      });
      setDoneIntent(selectedIntent);
      setSelectedIntent(null);
      if (!hasProfile) {
        resetWalkInGuestCheckinState();
      }
    } catch (err) {
      setError(err?.message || 'Check-in failed');
    } finally {
      setBusy(false);
    }
  }, [
    canConfirm,
    displayName,
    faceFromThisVisit,
    hasProfile,
    profileBundle,
    selectedIntent,
    walkInMobile,
    walkInName,
  ]);

  const startAnotherGuest = () => {
    resetWalkInGuestCheckinState();
    setDoneIntent(null);
    setSelectedIntent(null);
    setWalkInName('');
    setWalkInMobile('');
    setError('');
    navigate('/nyafaz-ep/salon/guest-intent', { replace: true, state: {} });
  };

  return (
    <div className="fi-lux-theme fi-scroll-page salon-visit-intent page-bg">
      <div className="salon-visit-intent__content">
        <header className="salon-visit-intent__brand">
          <FaceIdentityBrandLogo
            showLogo={showLogo}
            orgLogoUrl={orgLogoUrl}
            onLogoError={onLogoError}
          />
          <FaceIdentityFlowNav showBack={false} showHome={false} />
        </header>

        {doneIntent ? (
          <>
            <h1 className="salon-visit-intent__title">You&apos;re checked in</h1>
            <p className="salon-visit-intent__lede">
              Please take a seat — your stylist will call you shortly.
            </p>
            <div className="salon-service-session__panel" style={{ textAlign: 'center' }}>
              <p className="salon-service-session__banner">
                {displayName} · {visitIntentLabel(doneIntent)}
              </p>
            </div>
            <button
              type="button"
              className="salon-service-session__primary salon-service-session__primary--wide"
              onClick={startAnotherGuest}
            >
              Check in another guest
            </button>
          </>
        ) : (
          <>
            <h1 className="salon-visit-intent__title">Guest check-in</h1>
            <p className="salon-visit-intent__lede">
              Enter your details, choose how we can help, then confirm to join the stylist list.
            </p>

            <div className="salon-service-session__panel salon-visit-intent__form">
              {hasProfile ? (
                <p className="salon-service-session__title" style={{ textAlign: 'left', fontSize: '1.1rem' }}>
                  {displayName}
                </p>
              ) : (
                <>
                  <label className="salon-service-session__subtitle" htmlFor="walk-in-name">
                    Your name
                  </label>
                  <input
                    id="walk-in-name"
                    className="salon-service-session__code-input"
                    style={{ letterSpacing: 'normal', fontSize: '1rem', maxWidth: '100%' }}
                    value={walkInName}
                    onChange={(e) => setWalkInName(e.target.value)}
                    placeholder="Full name"
                    autoComplete="off"
                    name="salon-walk-in-name"
                  />
                  <label className="salon-service-session__subtitle" htmlFor="walk-in-mobile">
                    Mobile (optional)
                  </label>
                  <input
                    id="walk-in-mobile"
                    className="salon-service-session__code-input"
                    style={{ letterSpacing: 'normal', fontSize: '1rem', maxWidth: '100%' }}
                    inputMode="tel"
                    value={walkInMobile}
                    onChange={(e) => setWalkInMobile(e.target.value)}
                    placeholder="Phone number"
                    autoComplete="off"
                    name="salon-walk-in-mobile"
                  />
                  <button type="button" className="salon-service-session__link" onClick={goScan}>
                    Identify with face scan instead
                  </button>
                </>
              )}
            </div>

            <section className="salon-visit-intent__choice" aria-label="Visit type">
              <h2 className="salon-visit-intent__choice-label">How can we help?</h2>
              <div className="salon-visit-intent__actions">
                <button
                  type="button"
                  className={`salon-visit-intent__card${
                    selectedIntent === VISIT_INTENT_SERVICE ? ' is-active' : ''
                  }`}
                  disabled={busy}
                  onClick={() => setSelectedIntent(VISIT_INTENT_SERVICE)}
                >
                  <span className="salon-visit-intent__card-title">Service list</span>
                  <span className="salon-visit-intent__card-hint">
                    Browse salon services and book with a stylist
                  </span>
                </button>
                <button
                  type="button"
                  className={`salon-visit-intent__card${
                    selectedIntent === VISIT_INTENT_ANALYZE ? ' is-active' : ''
                  }`}
                  disabled={busy}
                  onClick={() => setSelectedIntent(VISIT_INTENT_ANALYZE)}
                >
                  <span className="salon-visit-intent__card-title">Skin &amp; hair analysis</span>
                  <span className="salon-visit-intent__card-hint">
                    Start a skin or hair analysis before your service
                  </span>
                </button>
              </div>
            </section>

            <button
              type="button"
              className="salon-service-session__primary salon-service-session__primary--wide salon-visit-intent__confirm"
              disabled={!canConfirm}
              onClick={confirmCheckin}
            >
              {busy
                ? 'Checking in…'
                : selectedIntent
                  ? `Confirm · ${visitIntentLabel(selectedIntent)}`
                  : 'Select an option, then confirm'}
            </button>
            {!nameReady && selectedIntent ? (
              <p className="salon-visit-intent__hint">Enter your name to continue.</p>
            ) : null}
            {error ? <p className="salon-service-session__error">{error}</p> : null}
          </>
        )}

        <div className="salon-visit-intent__desk">
          <button
            type="button"
            className="salon-visit-intent__desk-btn"
            onClick={() => {
              setSalonKioskMode(KIOSK_MODE_DESK);
              navigate(SALON_SERVICE_LOBBY_PATH);
            }}
          >
            Stylist desk
          </button>
        </div>
      </div>
    </div>
  );
}
